<template>
    <div
        class="cardBase"
        ref="experienceTemplateRef"
        :class="[
            $style.cardBase,
            props.item?.tags.layout === 'knowledge'
                ? 'ecCard'
                : props.item?.tags.layout === 'collection'
                ? 'ecCollection'
                : '',
            !visible && $style.loading
        ]"
        pt-loading-background="transparent"
        v-loading="!visible"
    >
        <template v-if="visible">
            <wac-component
                v-if="item?.isfromEC"
                :asset-id="item?.id"
                :component-data="item?.data"
                :displayMode="
                    item?.tags.layout === 'detail' || item?.tags.layout === 'instruction'
                        ? 'card'
                        : item?.tags.layout
                "
                :lang="wacLang"
                @preview="emit('view', $event)"
                @view="emit('info', $event)"
                @create="emit('create', $event)"
            ></wac-component>
            <TemplateCard
                v-if="!item?.isfromEC"
                :src="item?.pcThumbnail || item?.mobileThumbnail"
                :template="props.item"
                :showLayer="props.showLayer"
                :isMobile="props.isMobile"
                @view="emit('view', $event)"
                @create="emit('create', $event)"
                @edit="emit('edit', $event)"
                @delete="emit('delete', $event)"
            >
            </TemplateCard>
        </template>
    </div>
</template>

<script lang="ts" setup>
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
import type { ECTemplate } from '@models/ecshop/asset';

import { onMounted, onUnmounted, ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import useEventBus from '@assets/plugins/eventBus';

import { useECTemplates } from '@models/ecshop/asset';
import { ECDataLocation } from '@experience/hooks/template.hooks';
import analyticsService from '@assets/services/analytics.service';
import 'intersection-observer';

import TemplateCard from './TemplateCard.vue';

// interface
interface Props {
    position: ECDataLocation;
    isMobile: boolean;
    showLayer?: boolean;
    item: ExperienceTemplateV4 | ECTemplate;
    index: number;
}

// props
const props = defineProps<Props>();

// emit
const emit = defineEmits(['edit', 'create', 'delete', 'view', 'info']);

// state
const { eventBus } = useEventBus();
const { wacLang } = useECTemplates();
const experienceTemplateRef = ref();
const visible = ref(false);
const updateVisibleFlagDebounce = debounce(updateVisibleFlag, 200);

// watch
watch(
    () => props.index,
    (val, oldVal) => {
        if (val !== oldVal) {
            updateVisibleFlag();
        }
    }
);

// onMounted
onMounted(() => {
    updateVisibleFlag();
    eventBus.$on('windowResize', updateVisibleFlagDebounce);
    eventBus.$on('windowScroll', updateVisibleFlagDebounce);
    eventBus.$on('experienceDialogScroll', updateVisibleFlagDebounce);
});

// onUnmounted
onUnmounted(() => {
    eventBus.$off('windowResize', updateVisibleFlagDebounce);
    eventBus.$off('windowScroll', updateVisibleFlagDebounce);
    eventBus.$off('experienceDialogScroll', updateVisibleFlagDebounce);
});

// function
function updateVisibleFlag() {
    if (visible.value || !experienceTemplateRef.value) return;

    if (experienceTemplateRef.value && !document.contains(experienceTemplateRef.value)) {
        eventBus.$off('windowResize', updateVisibleFlagDebounce);
        eventBus.$off('windowScroll', updateVisibleFlagDebounce);
        eventBus.$off('experienceDialogScroll', updateVisibleFlagDebounce);
        return;
    }

    setTimeout(() => {
        if (!experienceTemplateRef.value) return;
        const innerHeight = window.innerHeight;
        const innerWidth = window.innerWidth;
        const { top, left, bottom, right } = experienceTemplateRef.value?.getBoundingClientRect();
        visible.value =
            ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
            ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth));
        sendImpressionEvent();
    });
}

function sendImpressionEvent() {
    if (visible.value) {
        let type = 'template';
        if (props.item?.isfromEC) {
            type = 'idea';
        }
        if (props.item?.tags?.layout === 'collection') {
            type = 'collection';
        }
        if (props.item?.tags?.layout === 'knowledge') {
            type = 'knowledge';
        }
        analyticsService
            .usePTX('gallery_item_impression', {
                item_type: type,
                item_name: props.item?.name,
                item_id: props.item?.id,
                position: props.position,
                is_blank: props.item?.id === 'blank' ? 'Y' : 'N'
            })
            .track();
    }
}
</script>

<style lang="scss" module>
@import '@style/import.scss';

.cardBase {
    min-width: 270px;
    min-height: 270px;
}

.loading {
    background-color: $pt-black-30;
    border-radius: 3px;
}
</style>
